/**
 * useEventMutations (Plan 03-08).
 *
 * TanStack Query mutation wrappers for calendar event CRUD.
 * Exposes `create`, `update`, and `remove` mutations that hit the API
 * endpoints defined in Plan 03-05 and invalidate the calendar query cache
 * on success.
 *
 * D-08: calendar is open-write; only requireSession middleware is used.
 * D-16: mutations are online-only; callers toast "Du er offline" before calling.
 * T-CAL-37: UI gate (Slett button) + API-enforces independently (defense in depth).
 * T-CAL-40: lead_minutes validated by Zod on server; frontend passes resolved values.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RecurrenceBuilder } from '@stell/shared';

export type EventInput = {
  title: string;
  description?: string | null;
  starts_at: string;
  ends_at?: string | null;
  all_day?: boolean;
  location?: string | null;
  color_token?: string | null;
  recurrence?: RecurrenceBuilder;
  attendee_ids: string[];
  reminders?: Array<{ member_id: string; lead_minutes: number }>;
};

export type Scope = 'single' | 'from_this' | 'series';

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['calendar', 'events'] });
  qc.invalidateQueries({ queryKey: ['calendar', 'today'] });
}

export function useEventMutations() {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: async (input: EventInput) => {
      const res = await fetch('/api/calendar/events', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(`create ${res.status}`);
      return res.json() as Promise<{ id: string }>;
    },
    onSuccess: () => invalidateAll(qc),
  });

  const update = useMutation({
    mutationFn: async (args: {
      id: string;
      input: Partial<EventInput>;
      scope: Scope;
      occurrence?: string;
    }) => {
      const url = new URL(`/api/calendar/events/${args.id}`, location.origin);
      url.searchParams.set('scope', args.scope);
      if (args.occurrence) url.searchParams.set('occurrence', args.occurrence);
      const res = await fetch(url.toString(), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(args.input),
      });
      if (!res.ok) throw new Error(`update ${res.status}`);
      return res.json();
    },
    onSuccess: () => invalidateAll(qc),
  });

  const remove = useMutation({
    mutationFn: async (args: { id: string; scope: Scope; occurrence?: string }) => {
      const url = new URL(`/api/calendar/events/${args.id}`, location.origin);
      url.searchParams.set('scope', args.scope);
      if (args.occurrence) url.searchParams.set('occurrence', args.occurrence);
      const res = await fetch(url.toString(), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`delete ${res.status}`);
      return res.json();
    },
    onSuccess: () => invalidateAll(qc),
  });

  return { create, update, remove };
}
